import * as Dialog from "@radix-ui/react-dialog";
import { ArchiveRestore, FileText, Folder, SquareCheckBig, Trash2, X } from "lucide-react";
import { create } from "zustand";
import { getData, useWorkspaceItems } from "@/data/store";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import { openItem, trashItem } from "@/commands/nav";
import { useUi } from "@/store/ui";
import s from "./TrashDialog.module.css";

interface ArchiveDialogStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useArchiveDialog = create<ArchiveDialogStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/** "3 days", "2 months", "1 year" — how long something has sat in the archive. */
export function sinceArchived(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86400_000);
  if (days < 1) return "today";
  if (days < 30) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"}`;
}

export function ArchiveDialog() {
  const { open, setOpen } = useArchiveDialog();
  const items = useWorkspaceItems();
  const activeApp = useUi((state) => state.activeApp);
  const tasks = activeApp === "tasks";
  // subtree roots only — children travel with their parent
  const archived = items
    .filter((i) =>
      tasks ? i.type === "tasklist" : i.type !== "card" && i.type !== "cardboard" && i.type !== "tasklist",
    )
    .filter((i) => i.archivedAt && !i.trashedAt)
    .filter((i) => {
      const parent = i.parentId ? items.find((p) => p.id === i.parentId) : null;
      return !parent?.archivedAt;
    })
    .sort((a, b) => (b.archivedAt ?? 0) - (a.archivedAt ?? 0));

  const restore = (id: string, title: string) => {
    getData().workspace.unarchive(id);
    toast.success(`Restored "${title || "Untitled"}"`, { label: "Open", onClick: () => openItem(id) });
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Dialog.Title className={s.title}>{tasks ? "Task list archive" : "Archive"}</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close">
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className={s.hint}>
            Archived items are kept forever, out of your main workspace and search. The age shows how long
            they've waited.
          </div>
          <div className={s.list}>
            {archived.length === 0 ? (
              <div className={s.empty}>{tasks ? "No task lists are archived." : "Nothing archived. Right-click any note or folder to archive it."}</div>
            ) : (
              archived.map((i) => (
                <div key={i.id} className={s.row}>
                  {i.type === "folder" ? <Folder size={14} /> : i.type === "tasklist" ? <SquareCheckBig size={14} /> : <FileText size={14} />}
                  <button type="button" className={s.rowTitle} style={{ textAlign: "left" }} onClick={() => openItem(i.id)}>
                    {i.title || "Untitled"}
                  </button>
                  <span className={s.rowDate}>archived {sinceArchived(i.archivedAt!)}</span>
                  <Button size="sm" variant="ghost" onClick={() => restore(i.id, i.title)}>
                    <ArchiveRestore size={12} /> Restore
                  </Button>
                  <IconButton label="Move to trash" onClick={() => trashItem(i.id)}>
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
